import styles from './chapter.module.css';
// The hero's stats read the shared stat sheet.
import { CampaignStats } from './campaign-stats';
import { ActivityRewards } from './activity-cards';
import { Hero } from './hero';
import { ProgressBar } from '../../../components/progress-bar';
import { ProgressPill } from '../progress-pill';
import { Pill } from '../../../components/pill';
import { activityKindLook } from '../../shell/activity-kinds';
import { Card } from '../../../components/card';
import { PageHead } from '../../../components/page-head';

import { useTranslation } from 'react-i18next';

import type {
  StudentCampaignDetail as Detail,
  StudentCampaignChapter,
  StudentActivity,
  StudentChapterMaterial,
} from '../../../api/student/campaigns/types';

import {
  activityState,
  chapterAvailability,
  chapterProgress,
  chapterStatus,
  mandatoryUnlocked,
  type ActivityState,
} from '../../../api/student/campaigns/gating';
import { ChapterProgressWords } from './chapter-progress';

/**
 * Chapter detail — the activities trail for one chapter, plus its extra
 * materials.
 */

interface ChapterDetailViewProps {
  campaign: Detail;
  chapter: StudentCampaignChapter;
  onBackToCampaign: () => void;
  onBackToList: () => void;
  onOpenActivity: (activityId: string) => void;
  onOpenMaterial: (materialId: string) => void;
}

/** Activity card body (type · title · rewards | status / CTA). Shared by the
 *  mandatory trail nodes and the optional loose cards. */
function ActivityCardButton({ activity, state, disabled, variant, onOpen }: {
  activity: StudentActivity;
  state: ActivityState;
  disabled: boolean;
  variant: 'trail' | 'opt';
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const cardCls = variant === 'opt' ? styles.opt : styles.node__card;
  const mainCls = variant === 'opt' ? styles.opt__main : styles.node__main;
  return (
    <Card padded={false} className={cardCls} onClick={onOpen} disabled={disabled}>
      <div className={mainCls}>
        <span className={`${styles.node__type} ${KIND_ACCENT[activity.type]}`}><i></i>{activityKindLook(activity.type, t).words}</span>
        <h3 className={`${styles.node__title}`}>{activity.title}</h3>
        <div className={`${styles.node__rewards}`}>
          <ActivityRewards activity={activity} />
        </div>
      </div>
      <div className={`${styles.node__aside}`}>
        {state === 'locked' ? (
          <span className={`${styles.statusLocked}`}>🔒 {t('student.status.activity.locked')}</span>
        ) : state === 'missed' ? (
          <span className={`${styles.statusMissed}`}>{t('student.campaign.gameplay.sessionClosed')}</span>
        ) : state === 'noSessions' ? (
          <span className={`${styles.statusEnded}`}>{t('student.status.activity.noSessions')}</span>
        ) : state === 'done' ? (
          <span className={`${styles.statusDone}`}>✓ {t('student.campaign.completed')}</span>
        ) : (
          <span className={`${styles.statusCta}`}>{t('student.campaign.start')} →</span>
        )}
      </div>
    </Card>
  );
}

/** A trail node's state as its module class, each named whole; a missed activity's node, and one with no more sessions, have no look of their own. */
const NODE_STATE: Partial<Record<ActivityState, string>> = {
  done: styles.nodeDone,
  current: styles.nodeCurrent,
  locked: styles.nodeLocked,
};

/** A node on the mandatory chapter trail: marker + connecting spine + card. */
function ActivityTrailNode({ activity, index, state, onOpen }: {
  activity: StudentActivity;
  index: number;
  state: ActivityState;
  onOpen: () => void;
}) {
  const marker = state === 'done' ? '✓' : state === 'locked' ? '🔒' : index + 1;
  return (
    <div className={`${styles.node} ${NODE_STATE[state] ?? ''}`}>
      <div className={`${styles.node__rail}`}><span className={`${styles.node__marker}`}>{marker}</span></div>
      <ActivityCardButton activity={activity} state={state} disabled={state === 'locked'} variant="trail" onOpen={onOpen} />
    </div>
  );
}

/** An activity's kind as its node shows it: an accent class each, its words
 *  the shared kind table's (activityKindLook). Explicit, because composing
 *  `t-${type}` at runtime is invisible to every check and to the build's scoping. */
const KIND_ACCENT: Record<StudentActivity['type'], string> = {
  ARTICLE: styles.tArticle,
  VIDEO: styles.tVideo,
  QUIZ: styles.tQuiz,
  GAMEPLAY: styles.tGameplay,
};

export function ChapterDetailView({ campaign, chapter, onBackToCampaign, onBackToList, onOpenActivity, onOpenMaterial }: ChapterDetailViewProps) {
  const campaignName = campaign.name;
  const { t } = useTranslation();
  const av = chapterAvailability(chapter, campaign);
  const status = chapterStatus(chapter, campaign);
  const progress = chapterProgress(chapter);

  const mandatoryActivities = chapter.activities.filter((a) => a.category === 'MANDATORY');
  const optionalActivities = chapter.activities.filter((a) => a.category === 'OPTIONAL');
  const materials = chapter.materials ?? [];


  return (
    <div className="page">
      <nav className="crumb">
        <button type="button" onClick={onBackToList}>{t('student.campaigns.title')}</button>
        <span className="sep">›</span>
        <button type="button" onClick={onBackToCampaign}>{campaignName}</button>
        <span className="sep">›</span>
        <span>{chapter.title}</span>
      </nav>

      <Hero size="chapter" image={chapter.imageUrl} badge={<ProgressPill of="chapter" status={status} />} title={chapter.title}>
        {chapter.description && <p className={`${styles.chHero__desc}`}>{chapter.description}</p>}
        <div className={`${styles.chHero__stats}`}>
          <CampaignStats earnedXp={progress.xpEarned} totalXp={progress.xpTotal} kpBalance={campaign.kpBalance} />
          <div className={`${styles.chHero__bar}`}>
            <ProgressBar value={progress.xpPct} grow />
            <span className={`${styles.chHero__words}`}>
              <ChapterProgressWords chapter={chapter} />
            </span>
          </div>
        </div>
      </Hero>

      {/* Mandatory — sequential connected trail */}
      {mandatoryActivities.length > 0 && (
        <>
          <PageHead level="section">{t('student.campaign.mandatoryActivities')}</PageHead>
          <div className={`${styles.trail}`}>
            {mandatoryActivities.map((a, idx) => {
              // Locked only while what comes before it is not done; the
              // student's own finished work reads done whatever holds.
              const state = activityState(a, mandatoryUnlocked(chapter, idx, campaign), campaign);
              return (
                <ActivityTrailNode
                  key={a.id}
                  activity={a}
                  index={idx}
                  state={state}
                  onOpen={() => state !== 'locked' && onOpenActivity(a.id)}
                />
              );
            })}
          </div>
        </>
      )}

      {/* Optional — loose cards (soft-locked, no spine) */}
      {optionalActivities.length > 0 && (
        <>
          <PageHead level="section">{t('student.campaign.optionalActivities')}</PageHead>
          <div className={`${styles.optGrid}`}>
            {optionalActivities.map((a) => {
              const state = activityState(a, av.chapterOpen, campaign);
              return (
                <ActivityCardButton
                  key={a.id}
                  activity={a}
                  state={state}
                  disabled={false}
                  variant="opt"
                  onOpen={() => onOpenActivity(a.id)}
                />
              );
            })}
          </div>
        </>
      )}

      {/* Extra materials */}
      {materials.length > 0 && (
        <>
          <PageHead level="section">{t('student.campaign.extraMaterials')}</PageHead>
          <div>
            {materials.map((m) => (
              <MaterialCard key={m.id} material={m} onOpen={() => onOpenMaterial(m.id)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

interface MaterialCardProps {
  material: StudentChapterMaterial;
  onOpen: () => void;
}

function MaterialCard({ material, onOpen }: MaterialCardProps) {
  const { t } = useTranslation();
  return (
    <button type="button" className={`${styles.mat}`} onClick={onOpen}>
      <Pill tone="active">{activityKindLook(material.type, t).words}</Pill>
      <span className={`${styles.mat__title}`}>{material.title}</span>
      <span className={`${styles.mat__cta}`}>→</span>
    </button>
  );
}

// ── Material detail sub-view ───────────────────

